import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { inject, observer } from "mobx-react";
import { getImage, getProductPageUrl } from "../utils/UrlUtils";
import { timeDiffBreakdown } from "../utils/DateUtils";
import {
  dealPrice,
  isDeal,
  isFlashDeal,
  productName,
  productPrice,
  remindMe,
  hasFlashDealStarted,
  isFlashDealExpired
} from "../utils/ProductUtils";
import { selectWebOrMobile } from "../utils/HelperUtils";
import ImageWithLoaderPart from "./ImageWithLoaderPart";
import AddToCartButtonPart from "./AddToCartButtonPart";
import { withSnackbar } from "notistack";
import TimerPart from "./TimerPart";
import gtmService from "../services/GTMService";
import { getCategoryName } from "../utils/CategoryUtils";
import { forceCheck } from "react-lazyload";
import { lazyLoadComponentInViewPort } from "../utils/lazyLoadComponent";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import { GBadge, GButton, GProductCard } from "./ui";
import {
  formatDiscount,
  formatPackSize,
  formatPrice
} from "../utils/FormatUtils";

@inject(["dealsStore"])
@inject(["productStore"])
@inject(["productsStore"])
@inject(["customerStore"])
@inject(["myOrdersStore"])
@inject(["featuredProductsStore"])
@inject(["userBasedRecommendationsStore"])
@observer
class ProductTilePart extends React.Component {
  componentDidMount() {
    forceCheck();
  }

  handleProductClick = product => {
    this.props.productStore.setProduct(product);
  };

  handleTileClick = () => {
    const {
      props: { product, listName, CTListName }
    } = this;

    if (CTListName && CTListName.split(" - ")[0] === "Horizontal Section") {
      gtmService.pageView("/horizontal-product-click", {
        name: CTListName.split(" - ")[1],
        "category id": product.category_id.toString(),
        "category name": getCategoryName(product.category_id)
      });
    }
    // fired by the image and name links
    this.handleProductClick(product);
    gtmService.productClick(product, listName);
  };

  refreshProduct = product => {
    const {
      props: {
        dealsStore,
        productsStore,
        myOrdersStore,
        featuredProductsStore,
        userBasedRecommendationsStore
      }
    } = this;
    [
      myOrdersStore.previouslyOrderedList,
      dealsStore.all,
      featuredProductsStore.all,
      userBasedRecommendationsStore.all,
      productsStore.all,
      productsStore.daalainAndRice,
      productsStore.fruitsAndVegetables
    ].forEach(arr => {
      arr.find(prd => {
        if (prd.id === product.id && prd.flash_deal) {
          prd.flash_deal.status =
            prd.flash_deal.status === 1
              ? 2
              : prd.flash_deal.status === 2
              ? 0
              : prd.flash_deal.status;
          return;
        }
      });
    });
  };

  discountLabel(product) {
    if (isFlashDeal(product)) {
      return formatDiscount(product.flash_deal.percentage);
    }
    if (isDeal(product)) {
      return product.deal_percentage
        ? formatDiscount(product.deal_percentage)
        : product.deal_title;
    }
    return null;
  }

  renderFlashBadge(product) {
    const upcoming = hasFlashDealStarted(product);
    if (!isFlashDeal(product) && !upcoming) {
      return null;
    }
    return (
      <GBadge tone="brand">
        <FlashOnIcon style={{ fontSize: 14 }} aria-hidden="true" />
        {upcoming ? "Flash deal in " : "Flash deal · "}
        <TimerPart
          beforeDeal={upcoming}
          initialTime={timeDiffBreakdown(
            upcoming
              ? product.flash_deal.start_time
              : product.flash_deal.end_time
          )}
          isDone={() => this.refreshProduct(product)}
        />
      </GBadge>
    );
  }

  render() {
    const {
      props: {
        product,
        productStore,
        nameCharLimit,
        listName,
        CTListName,
        state,
        query
      }
    } = this;

    if (!product.name) {
      return null;
    }

    const outOfStock = product.vendor_status === 0;
    const onSale =
      isFlashDeal(product) || isDeal(product) || !isFlashDealExpired(product);
    const notified = productStore.notifiedItems.includes(
      product.vendor_product_id
    );

    const productCard = (
      <GProductCard
        to={getProductPageUrl(product)}
        onClick={this.handleTileClick}
        outOfStock={outOfStock}
        image={
          <ImageWithLoaderPart
            alt={product.name}
            src={getImage(product)}
            width="130"
            height="130"
            state={state}
          />
        }
        name={productName(product.name, nameCharLimit)}
        price={formatPrice(onSale ? dealPrice(product) : productPrice(product))}
        wasPrice={onSale ? formatPrice(productPrice(product)) : null}
        discount={this.discountLabel(product)}
        topBadge={this.renderFlashBadge(product)}
        packSize={formatPackSize(product.unit)}
        action={
          !outOfStock && (
            <AddToCartButtonPart
              listName={listName}
              query={query}
              CTListName={CTListName}
              product={product}
            />
          )
        }
        footer={
          outOfStock && (
            <GButton
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => remindMe(this.props, product)}
              disabled={notified}
            >
              {notified ? "We'll notify you" : "Notify me"}
            </GButton>
          )
        }
      />
    );

    return lazyLoadComponentInViewPort(productCard, {
      once: true,
      resize: true,
      height: 280,
      style: { height: "100%" }
    });
  }
}

ProductTilePart.propTypes = {
  product: PropTypes.object.isRequired,
  isHorizontal: PropTypes.bool,
  nameCharLimit: PropTypes.func | PropTypes.number,
  listName: PropTypes.string,
  CTListName: PropTypes.string,
  query: PropTypes.string
};
ProductTilePart.defaultProps = {
  isHorizontal: false,
  nameCharLimit: selectWebOrMobile(55, 30, 100)
};

export default withRouter(withSnackbar(ProductTilePart));
